<template>
  <div class="transactions-panel border rounded p-3 h-100">
    <div class="d-flex justify-content-between align-items-center gap-2 mb-2">
      <h3 class="h6 mb-0">Transactions</h3>
      <BButton
        variant="primary"
        :disabled="saveDisabled"
        @click="$emit('save-all-changes')"
      >
        {{ savingTracking ? "Saving..." : "Save All Changes" }}
      </BButton>
    </div>

    <div
      v-if="draftValidationMessages.length > 0"
      class="alert alert-warning py-2"
    >
      <div
        v-for="message in draftValidationMessages"
        :key="message"
        class="small"
      >
        {{ message }}
      </div>
    </div>

    <div v-if="transactionRows.length === 0" class="text-muted">
      No transactions found for this account.
    </div>

    <div
      v-for="row in paginatedTransactionRows"
      :key="row.transaction_id"
      class="transaction-row border rounded p-2 mb-2"
      :class="{
        'row-error': row.status === 'error',
        'row-ok': row.status === 'categorized',
      }"
    >
      <div class="d-flex justify-content-between align-items-center gap-2">
        <div class="d-flex align-items-center gap-2">
          <button
            v-b-tooltip.click
            type="button"
            class="date-chip border-0 bg-transparent p-0"
            :title="getTransactionDateInfo(row).fullDate"
            aria-label="View full transaction date"
          >
            <IconWashDry class="date-chip-icon" :size="28" />
            <span class="date-chip-day">{{
              getTransactionDateInfo(row).day
            }}</span>
          </button>

          <div
            v-b-tooltip.click
            :title="getTransactionNameInfo(row).tooltipText"
            class="fw-semibold"
          >
            {{ getTransactionNameInfo(row).name }}
          </div>
        </div>
        <div class="d-flex align-items-center gap-2">
          <div class="d-flex align-items-center gap-2 text-end">
            <small class="text-muted">
              Allocated: ${{ row.totalAllocated.toFixed(2) }}
            </small>
            <div class="fw-semibold">${{ row.amount.toFixed(2) }}</div>
          </div>

          <BButton
            class="row-action-btn"
            size="sm"
            :variant="rowActionVariant(row)"
            :disabled="isRowActionDisabled(row)"
            :title="rowActionTitle(row)"
            @click="onRowActionClick(row)"
          >
            <IconPlus v-if="row.status === 'uncategorized'" :size="14" />
            <IconCheck v-else-if="row.status === 'categorized'" :size="14" />
            <IconAlertTriangle v-else :size="14" />
          </BButton>
        </div>
      </div>

      <div>
        <ul
          v-if="row.allocations.length > 0"
          class="list-group list-group-flush my-2"
        >
          <li
            v-for="allocation in row.allocations"
            :key="allocation.tsxn_uid"
            class="list-group-item px-0 py-1 d-flex justify-content-between small"
          >
            <span>{{
              categoryNameByUid(allocation.spending_category_uid)
            }}</span>
            <strong
              >${{ Number(allocation.fixed_amount || 0).toFixed(2) }}</strong
            >
          </li>
        </ul>

        <div v-if="row.status !== 'categorized'">
          <div
            v-for="draft in draftsForTransaction(row.transaction_id)"
            :key="draft.localId"
            class="row g-2 align-items-end mb-2"
          >
            <div class="col-md-6">
              <BFormGroup label="Category" class="mb-0 floating-group">
                <BFormSelect
                  :model-value="draft.spendingCategoryUid"
                  :options="categoryOptions"
                  @update:modelValue="
                    $emit('update-draft', {
                      transactionId: row.transaction_id,
                      localId: draft.localId,
                      key: 'spendingCategoryUid',
                      value: $event,
                    })
                  "
                />
              </BFormGroup>
            </div>
            <div class="col-md-4">
              <BFormGroup label="Amount" class="mb-0 floating-group">
                <BFormInput
                  type="number"
                  min="0"
                  step="0.01"
                  :model-value="draft.fixedAmount"
                  @update:modelValue="
                    $emit('update-draft', {
                      transactionId: row.transaction_id,
                      localId: draft.localId,
                      key: 'fixedAmount',
                      value: $event,
                    })
                  "
                />
              </BFormGroup>
            </div>
            <div class="col-md-2">
              <BButton
                variant="outline-danger"
                class="w-100"
                @click="
                  $emit('remove-draft', {
                    transactionId: row.transaction_id,
                    localId: draft.localId,
                  })
                "
              >
                <IconTrash :size="14" />
              </BButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="transactionRows.length > perPage"
      class="d-flex justify-content-center mt-3"
    >
      <BPagination
        v-model="currentPage"
        :total-rows="transactionRows.length"
        :per-page="perPage"
        size="sm"
      />
    </div>
  </div>
</template>

<script>
import {
  BPagination,
  BButton,
  BFormGroup,
  BFormInput,
  BFormSelect,
  vBTooltip,
} from "bootstrap-vue-next";
import {
  IconAlertTriangle,
  IconWashDry,
  IconCheck,
  IconPlus,
  IconTrash,
} from "@tabler/icons-vue";

const ROUNDING_TOLERANCE = 0.01;

export default {
  name: "TransactionsView",
  data() {
    return {
      currentPage: 1,
      perPage: 10,
    };
  },
  components: {
    BPagination,
    BButton,
    BFormGroup,
    BFormInput,
    BFormSelect,
    IconAlertTriangle,
    IconWashDry,
    IconCheck,
    IconPlus,
    IconTrash,
  },
  directives: {
    BTooltip: vBTooltip,
  },
  emits: ["add-draft", "update-draft", "remove-draft", "save-all-changes"],
  props: {
    financialTransactions: {
      type: Array,
      default: () => [],
    },
    categorizedByPlaidTransactionUid: {
      type: Object,
      default: () => ({}),
    },
    selectedSnapshotUid: {
      type: String,
      default: null,
    },
    savingTracking: {
      type: Boolean,
      default: false,
    },
    categoryOptions: {
      type: Array,
      default: () => [],
    },
    snapshotCategories: {
      type: Array,
      default: () => [],
    },
    draftsByTransactionId: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    drafts() {
      return Object.values(this.draftsByTransactionId).flat();
    },
    transactionRows() {
      const rows = this.financialTransactions.map((transaction) => {
        const allocations =
          this.categorizedByPlaidTransactionUid[transaction.transaction_id] ||
          [];
        const totalAllocated = allocations.reduce(
          (sum, item) => sum + Number(item.fixed_amount || 0),
          0
        );
        const amount = Number(transaction.amount || 0);

        let status = "uncategorized";
        if (allocations.length > 0) {
          status =
            Math.abs(totalAllocated - amount) > ROUNDING_TOLERANCE
              ? "error"
              : "categorized";
        }

        return {
          ...transaction,
          amount,
          allocations,
          totalAllocated,
          status,
        };
      });

      return rows.sort((a, b) => {
        // Keep fully categorized rows at the end so actionable rows stay visible.
        if (a.status === "categorized" && b.status !== "categorized") {
          return 1;
        }

        if (a.status !== "categorized" && b.status === "categorized") {
          return -1;
        }

        if (a.status === "error" && b.status !== "error") {
          return -1;
        }

        if (a.status !== "error" && b.status === "error") {
          return 1;
        }

        return (
          new Date(b.effective_date || 0) - new Date(a.effective_date || 0)
        );
      });
    },
    paginatedTransactionRows() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.transactionRows.slice(start, end);
    },
    draftValidationMessages() {
      const messages = [];

      for (const transaction of this.financialTransactions) {
        const transactionId = transaction.transaction_id;
        const drafts = this.draftsForTransaction(transactionId);
        if (drafts.length === 0) {
          continue;
        }

        const allocations =
          this.categorizedByPlaidTransactionUid[transactionId] || [];
        const totalAllocated = allocations.reduce(
          (sum, item) => sum + Number(item.fixed_amount || 0),
          0
        );
        const amount = Number(transaction.amount || 0);

        const missingCategory = drafts.some(
          (draft) => !draft.spendingCategoryUid
        );
        if (missingCategory) {
          messages.push(
            `Transaction ${transaction.name}: each draft needs a category.`
          );
        }

        const existingCategoryUids = allocations
          .map((allocation) => allocation.spending_category_uid)
          .filter(Boolean);
        const draftCategoryUids = drafts
          .map((draft) => draft.spendingCategoryUid)
          .filter(Boolean);
        const combinedCategoryUids = [
          ...existingCategoryUids,
          ...draftCategoryUids,
        ];
        const hasDuplicateCategories =
          new Set(combinedCategoryUids).size !== combinedCategoryUids.length;

        if (hasDuplicateCategories) {
          messages.push(
            `Transaction ${transaction.name}: each category can only be used once.`
          );
        }

        const invalidAmount = drafts.some((draft) =>
          Number.isNaN(Number(draft.fixedAmount))
        );
        if (invalidAmount) {
          messages.push(
            `Transaction ${transaction.name}: each draft amount must be a valid number.`
          );
        }

        const draftTotal = drafts.reduce(
          (sum, draft) => sum + Number(draft.fixedAmount || 0),
          0
        );
        const finalTotal = totalAllocated + draftTotal;

        if (Math.abs(finalTotal - amount) > ROUNDING_TOLERANCE) {
          messages.push(
            `Transaction ${
              transaction.name
            }: allocated total must equal $${amount.toFixed(2)}.`
          );
        }
      }

      return [...new Set(messages)];
    },
    saveDisabled() {
      return (
        this.savingTracking ||
        this.drafts.length === 0 ||
        this.draftValidationMessages.length > 0 ||
        !this.selectedSnapshotUid
      );
    },
  },
  watch: {
    transactionRows(rows) {
      const totalPages = Math.max(1, Math.ceil(rows.length / this.perPage));
      if (this.currentPage > totalPages) {
        this.currentPage = totalPages;
      }
    },
  },
  methods: {
    getTransactionNameInfo(row) {
      const tooltipText =
        row.merchant_name || row.name || "Unnamed transaction";

      const name =
        tooltipText.length > 20
          ? `${tooltipText.substring(0, 20)}...`
          : tooltipText;

      return {
        name,
        tooltipText,
      };
    },
    getTransactionDateInfo(row) {
      const fullDate = row.effective_date || "Unknown date";
      const day = /^\d{4}-\d{2}-\d{2}$/.test(fullDate)
        ? fullDate.split("-")[2]
        : fullDate;

      return {
        day,
        fullDate,
      };
    },
    draftsForTransaction(transactionId) {
      return this.draftsByTransactionId[transactionId] || [];
    },
    categoryNameByUid(categoryUid) {
      const match = this.snapshotCategories.find(
        (category) =>
          (category.category_uid || category.categoryUid) === categoryUid
      );
      return match?.name || categoryUid;
    },
    rowActionVariant(row) {
      if (row.status === "categorized") {
        return "outline-success";
      }

      if (row.status === "error") {
        return "outline-danger";
      }

      return "outline-secondary";
    },
    isRowActionDisabled(row) {
      return row.status === "categorized";
    },
    rowActionTitle(row) {
      if (row.status === "categorized") {
        return "Transaction categorized";
      }

      if (row.status === "error") {
        return "Transaction needs attention. Add a category split.";
      }

      return "Add category split";
    },
    onRowActionClick(row) {
      if (this.isRowActionDisabled(row)) {
        return;
      }

      this.$emit("add-draft", row);
    },
  },
};
</script>

<style scoped>
.transactions-panel {
  background: #fff;
}

.row-action-btn {
  min-width: 2rem;
  min-height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
}

.date-chip {
  position: relative;
  width: 1.9rem;
  height: 1.9rem;
  color: #6c757d;
  line-height: 1;
}

.date-chip-icon {
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0.45;
}

.date-chip-day {
  position: absolute;
  top: 52%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.72rem;
  font-weight: 700;
  color: #212529;
}

.transaction-row.row-error {
  border-color: #dc3545 !important;
  background: #fff5f5;
}

.transaction-row.row-ok {
  border-color: #198754 !important;
  background: #f3fff8;
}
</style>
